























import ErrorDialog from '@/components/organisms/dialog/ErrorDialog.vue'
import { ApiCreditcard } from '@/models/api/creditcard'
import { Payment, PaymentList } from '@/models/api/payment/payment'
import { ContractedSubscription } from '@/models/order/subscription/contracted/contracted-subscription'
import { ContractedSubscriptionFindRepository } from '@/models/order/subscription/contracted/contracted-subscription-repository'
import { paymentWithCreditCard } from '@/models/order/subscription/contracted/payment'
import { DefaultContractedSubscriptionRepository } from '@/models/order/subscription/contracted/repository/default/default-contracted-subscription-repository'
import { ApiClientWithAuth } from '@/services/api/client'
import PaymentService from '@/services/api/payment'
import { SubscriptionService } from '@/services/api/subscription'
import { CreditcardServiceForSubscription } from '@/services/api/subscription/payment/creditcard-service-for-subscription'
import { getCreditcardPaymentProvierServiceConfig } from '@/services/payment'
import { adCodeStore } from '@/util/adcode/store'
import PaymentEditViewModel, { PaymentEditViewModelFactory } from '@spa/components/organisms/order/edit/payment/model/payment-edit-view-model'
import PaymentEdit from '@spa/components/organisms/order/edit/payment/PaymentEdit.vue'
import { SubmitChangePayment } from '@spa/components/pages/mypage/subscription/contracted/detail/edit/payment/interaction/submit-change-payment'
import { SubmitChangePaymentDefault } from '@spa/components/pages/mypage/subscription/contracted/detail/edit/payment/interaction/submit/submit-change-payment-default'
import { SubmitChangeToCreditCard } from '@spa/components/pages/mypage/subscription/contracted/detail/edit/payment/interaction/submit/submit-change-to-credit-card'
import { log } from '@spa/log'
import { Status } from '@spa/mixins/loading'
import { PluginMixin } from '@spa/mixins/plugin'
import { LoadPayload } from '@spa/store/modules/creditcard/actions'
import { CreditcardModel } from '@spa/store/modules/creditcard/types'
import { User } from '@spa/store/modules/user/types'
import { none, Option, some } from 'fp-ts/lib/Option'
import _ from 'lodash'
import { Mixins } from 'vue-mixin-decorator'
import { Component, Prop } from 'vue-property-decorator'

@Component({
  components: {
    PaymentEdit,
    ErrorDialog,
  },
})
export default class EditPaymentForSubscription extends Mixins<PluginMixin>(PluginMixin) {
  @Prop({ required: true })
  readonly id: string

  @Prop({ default: () => new DefaultContractedSubscriptionRepository(ApiClientWithAuth) })
  readonly contractedSubscriptionRepository: ContractedSubscriptionFindRepository

  readonly subscriptionApiServiceViaLegacyProxy: SubscriptionService = new SubscriptionService()
  readonly paymentService = new PaymentService()
  readonly creditcardService = new CreditcardServiceForSubscription()

  subscription: Option<ContractedSubscription> = none

  status: Status = Status.Pending

  selectedCard: ApiCreditcard | null = null

  securityCode: string = ''

  showModal: boolean = false
  modalMessage: string = ''

  paidyHandler: PaidyHandler

  paymentType: string = ''
  availablePaymentList: PaymentList = PaymentList.empty()

  get cardPaymentProviderConfig() {
    return getCreditcardPaymentProvierServiceConfig()
  }
  get cardPaymentProvider() {
    return this.cardPaymentProviderConfig.forSubscription
  }

  // TODO: Navigation guard against user status
  async created() {
    this.status = Status.Loading

    try {
      const handleError = (modalMessage: string) => (error: Error) => {
        this.modalMessage += modalMessage

        log.error(error)

        throw error
      }
      await Promise.all([
        // User info is required for Atone modal. Basically user is already loaded before this page is rendered.
        this.$store.dispatch('user/get'),
        this.loadCreditCards({ force: false }),
        this.contractedSubscriptionRepository.findById(this.id).then(contracted => new Promise((resolve, reject) => {
          if (!contracted.isEnabled()) {
            // 画面遷移前にロードが完了して、一瞬編集画面が見えるなどを防ぐため、pushにresolve/rejectを指定
            this.$router.push(`/mypage/subscription/${this.id}`, resolve, reject)

            return
          }

          this.subscription = some(contracted)
          resolve()
        })).catch(handleError('購入履歴が取得できませんでした。<br/>')),
        this.paymentService.getByContractedSubscriptionId(this.id).then(result => result.fold(
          handleError('利用可能なお支払い方法が取得できませんでした。<br/>'),
          (list) => {
            this.availablePaymentList = PaymentList.valueOf(list)
          }
        )),
        this.cardPaymentProvider.loadScript().catch(handleError('クレジットカードのご利用を有効にできませんでした。<br/>')),
      ])

      // Set selected payment info
      this.initPaymentType()

      this.status = Status.Pending
    } catch (error) {
      this.modalMessage += '<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
      this.showModal = true
      this.status = Status.Failed

      log.error(error)
    }
  }

  get isLoading() {
    return this.status === Status.Loading || this.subscription.isNone()
  }

  get isPageCompleted(): boolean {
    return this.status === Status.Pending && this.subscription.isSome()
  }

  get shipping(): number {
    return this.subscription.fold(0, s => s.payment.deliveryFee || 0)
  }

  get initialOrderPaymentId(): string {
    return this.subscription.fold('', s => s.payment.method)
  }

  get selectedPaymentTypeIsAllowed(): boolean {
    return this.availablePaymentList.isAvailablePayment(this.paymentType)
  }

  get selectedCardId(): string | undefined {
    return _.get(this.selectedCard, 'id')
  }

  get creditcards(): CreditcardModel[] {
    return this.$store.state.creditcard.items
  }

  get creditcardLoadFailed(): boolean {
    const errorState = _.get(this.$store.state.creditcard, 'error')
    const loadError = _.get(errorState, 'load', null)

    return loadError !== null
  }

  get user(): User {
    return this.$store.state.user.user
  }

  get adCode(): string | null {
    return adCodeStore.read().toNullable()
  }

  get showSecurityCode(): boolean {
    return this.paymentType === 'creditcard'
      && this.cardPaymentProviderConfig.viewConfig.paymentForm.requireSecurityCode
  }

  get cancelUrl(): string {
    return `/mypage/order/${this.id}`
  }

  get registerCardLimit(): number | null {
    return this.cardPaymentProvider.registerCardLimit
  }

  /*
   * 単発注文・定期購入・定期購入支払い方法変更で代わるのは専ら以下の三つ?
   * - paymentEditViewModel
   * - context
   * - 決定時の動作 = submit
   * DIなりの方法を用いて、これらを各画面ごとに動的に指定するようにすれば、
   * 複数画面で単一のコンポーネントを再利用できる？
   */
  get paymentEditViewModel(): PaymentEditViewModel {
    const factory = new PaymentEditViewModelFactory(
      this.availablePaymentList,
      this.creditcards,
      this.useCreditcard,
      this.removeCreditcard,
      this.navigateToNewCreditCard,
      this.showSecurityCode
    )
    factory.selectedPaymentId = this.paymentType
    factory.creditCardSelectedId = this.selectedCardId || ''
    factory.creditCardLoadFailed = this.creditcardLoadFailed
    /**
     * @see https://github.com/my-color/front/issues/5817 1指定の理由
     * @see https://github.com/my-color/front/pull/5819
     */
    factory.creditCardRegisterLimit = 1
    factory.submitButtonLabel = 'お支払い方法を変更する'
    factory.cancelButtonLabel = 'お申し込み履歴に戻る'
    factory.showCancelButton = false // 解約可能になったらtrueにする

    return factory.create()
  }

  /**
   * NOTE: 配送方法によって特定の決済方法が選択出来ない場合があるので、そのロジックを実装するために使う
   * NOTE: 美ラボ・ホテラバリリース後、リファクタリングしてこのプロパティを削除する必要があるか?
   * @see: https://github.com/my-color/front/issues/5077
   */
  get deliveryMethod(): string {
    return this.subscription.fold('', s => s.delivery.method.name)
  }

  initPaymentType(): void {
    const paymentType = this.subscription.fold('', subscription => subscription.payment.method)

    if (!this.isAvailablePayment(paymentType)) {
      return
    }

    // Creditcard requires a particular setting unlike the rest types.
    if (paymentType === 'creditcard') {
      this.initPaymentTypeToCreditcard()

      return
    }

    // For other payment types than creditcards
    this.paymentType = paymentType
  }

  initPaymentTypeToCreditcard(): void {
    const subscription = this.subscription
    if (!subscription.isSome()) return

    const payment = subscription.value.payment
    if (!paymentWithCreditCard(payment)) {
      return
    }

    const creditcardId = payment.method_detail.token

    const card = this.creditcards.find(c => c.id === creditcardId)
    // It might occur that card does not exist because the card can have been removed after the original order.
    if (!card) {
      log.warn(`credit card ${creditcardId} not found in cards already fetched`)

      return
    }

    this.useCreditcard(card)
  }

  isAvailablePayment(paymentId: string) {
    return this.availablePaymentList.isAvailablePayment(paymentId)
  }

  async submit() {
    if (this.status !== Status.Pending) {
      return
    }

    this.status = Status.Loading

    try {
      // Validate fields
      const validated = await this.$validator.validateAll()

      if (!validated) {
        this.modalMessage += '入力内容が正しくありません'
        this.showModal = true
        this.status = Status.Pending

        return false
      }

      // Validate payment type
      if (!this.paymentType) {
        this.modalMessage = 'お支払い方法が選択されていません'
        this.showModal = true
        this.status = Status.Pending

        return
      }

      if (!this.selectedPaymentTypeIsAllowed) {
        this.modalMessage = 'このお支払い方法はご利用頂けません'
        this.showModal = true
        this.status = Status.Pending

        return
      }

      const result = await this.submitChangePayment.submit(this.id, {
        payment_type: this.paymentType,
      })

      result.fold(
        (error) => {
          this.modalMessage += 'エラーが発生しました'

          throw error
        },
        () => null
      )

      this.status = Status.Completed
      this.$router.push(`/mypage/subscription/${this.id}`)
    } catch (error) {
      log.error(error)

      this.modalMessage += '<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
      this.showModal = true

      this.status = Status.Pending
    }
  }

  useCreditcard(creditcard: CreditcardModel): () => Promise<void> {
    return async () => {
      this.paymentType = 'creditcard'
      this.selectedCard = creditcard
    }
  }
  removeCreditcard(creditcard: CreditcardModel): () => Promise<void> {
    return async () => {
      try {
        if (!confirm('このカード情報を削除してもよろしいですか？')) {
          return
        }

        this.status = Status.Loading
        const { id: creditcardId } = creditcard

        await this.$store.dispatch('creditcard/remove', creditcardId)

        const errorOnRemove = _.find(this.$store.state.creditcard.items, ['id', creditcardId])

        if (errorOnRemove) {
          this.modalMessage += 'エラーが発生しました。<br/>クレジットカード情報が削除できませんでした。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
          this.showModal = true

          return
        }

        await this.loadCreditCards({ force: true })

        // Update Vue data
        if (this.paymentType === 'creditcard' && this.selectedCardId === creditcardId) {
          this.paymentType = ''
          this.selectedCard = null
        }
      } catch (error) {
        log.error(error)

        this.modalMessage += 'エラーが発生しました。<br/>※改善されない場合、お問い合わせよりカスタマーセンターまでお問い合わせください。'
        this.showModal = true
      } finally {
        this.status = Status.Pending
      }
    }
  }

  navigateToNewCreditCard() {
    this.$router.push(`/mypage/subscription/${this.id}/edit/payment/new-creditcard`)
  }

  navigateToPreviousPage() {
    this.$router.push(this.cancelUrl)
  }

  onPaymentSelected(payment: Payment) {
    this.selectedCard = null
    this.paymentType = payment.id
  }

  onUpdateCreditcardSecurityCode(code: string) {
    this.securityCode = code
  }

  onPaymentEditError(message: string) {
    this.modalMessage = message
    this.showModal = true
  }

  closeModal() {
    this.showModal = false
    this.modalMessage = ''
  }

  private get submitChangePayment(): SubmitChangePayment {
    switch (this.paymentType) {
      case 'creditcard':
        return new SubmitChangeToCreditCard(
          this.cardPaymentProvider,
          this.subscriptionApiServiceViaLegacyProxy,
          this.selectedCard!.withSecurityCode(this.securityCode),
          this.user
        )
      default:
        return new SubmitChangePaymentDefault(this.subscriptionApiServiceViaLegacyProxy)
    }
  }
  private loadCreditCards(opt: { force: boolean }): Promise<void> {
    const payload: LoadPayload = {
      ...opt,
      service: this.creditcardService,
    }

    return this.$store.dispatch('creditcard/load', payload)
  }
}
